/**
 * TipTap extension that renders full-width paragraph highlights with React portal support.
 *
 * This extension uses ProseMirror's decoration system to:
 * - Add node decorations for full-width background colors on paragraphs
 * - Add widget decorations for React portal containers at highlight end positions
 *
 * The extension stores highlight context that can be consumed by React components
 * via the useHighlightPortals hook.
 *
 * @module ParagraphHighlightExtension
 */

import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, type Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';

/**
 * Supported highlight types for paragraph highlighting. These paint a
 * full-width background row plus a portal widget.
 */
export type TParagraphHighlightType =
  | 'exception'
  | 'resolvedException'
  | 'running';

/**
 * The paragraph highlight types the plugin paints, as a runtime list.
 *
 * The decoration pass filters on this instead of comparing against hard-coded
 * type names: a type added to {@link TParagraphHighlightType} but missing from
 * the filter type-checks fine and is then silently dropped at runtime.
 */
export const PARAGRAPH_HIGHLIGHT_TYPES: readonly TParagraphHighlightType[] = [
  'exception',
  'resolvedException',
  'running',
];

/**
 * Types whose portal widget is placed *after* the highlighted range's last
 * block, rather than inline at the end of its last paragraph.
 *
 * A step's last paragraph is often inside a blockquote (an AOP callout), and a
 * widget anchored inside it renders as part of the quote. These types get a
 * single widget positioned after that block instead, so the portal is a sibling
 * of the step's content and reads as belonging to the step as a whole.
 */
const AFTER_BLOCK_PORTAL_TYPES: readonly TParagraphHighlightType[] = [
  'running',
];

/**
 * Document position just after the block containing `paragraph`.
 *
 * Three cases:
 * - inside a list item: after that item, so the widget follows the step rather
 *   than the whole list
 * - nested in a wrapper (a blockquote AOP callout): after the top-level ancestor,
 *   so the widget escapes the quote
 * - a top-level paragraph: after the paragraph itself. `paragraph.pos` resolves
 *   to depth 0 here, so `after(1)` does not apply — using the position itself
 *   would insert the widget *above* the paragraph.
 *
 * @param doc - The ProseMirror document
 * @param paragraph - The paragraph to escape from
 * @returns Position after the containing block
 */
function positionAfterContainingBlock(
  doc: ProseMirrorNode,
  paragraph: IParagraphInfo,
): number {
  if (paragraph.listItemEnd !== undefined) return paragraph.listItemEnd;
  const $pos = doc.resolve(paragraph.pos);
  return $pos.depth > 0 ? $pos.after(1) : paragraph.end;
}

/**
 * Kinds of portal widget that can be rendered into the editor.
 *
 * Highlights ({@link TParagraphHighlightType}) draw a background row; anchors
 * (`browserOperator`) draw *only* the portal widget pinned to a step and never
 * a background. Anchors live in their own decoration source and are independent
 * of the hover/exception highlight set — see {@link IAnchorWidget}.
 */
export type TPortalContextType = TParagraphHighlightType | 'browserOperator';

/**
 * Source of the highlight, used to determine badge behavior.
 * - 'activeException': Highlight from active exception (awaiting_guidance state)
 * - 'runLog': Highlight from RunLog hover/select interaction
 */
export type THighlightSource = 'activeException' | 'runLog';

/**
 * Character range in plain text coordinates.
 */
export interface ICharacterRange {
  /** Start character offset (inclusive) */
  start: number;
  /** End character offset (exclusive) */
  end: number;
}

/**
 * Context for a highlighted paragraph, passed to React components.
 */
export interface IHighlightContext {
  /** Unique identifier for this highlight */
  id: string;
  /** The type of highlight or anchor widget */
  type: TPortalContextType;
  /** Start position of the paragraph in the document */
  paragraphPos: number;
  /** End position of the paragraph in the document */
  paragraphEnd: number;
  /** The text content of the highlighted paragraph */
  text: string;
  /** The original character range that triggered this highlight */
  originalRange: ICharacterRange;
  /** Index of the paragraph in the document (0-based) */
  paragraphIndex: number;
  /** Whether this is the last paragraph in the highlight range */
  isLastInRange: boolean;
  /** Source of the highlight (activeException or runLog) */
  source?: THighlightSource;
}

/**
 * A typed highlight with its ranges.
 */
export interface ITypedParagraphHighlight {
  /** The type of highlight */
  type: TParagraphHighlightType;
  /** Character ranges to highlight (in plain text coordinates) */
  ranges: ICharacterRange[];
  /** Source of the highlight (activeException or runLog) */
  source?: THighlightSource;
}

/**
 * A sticky widget anchored to a step, rendered as a portal with no background
 * row. Anchors are held in a separate decoration source from highlights, so
 * they survive the constant churn of hover/exception {@link setHighlights}
 * calls without any merge logic. Used for the inline "Browser Operator" pill.
 */
export interface IAnchorWidget {
  /** Stable identifier (e.g. the browser session key) — must be unique. */
  id: string;
  /** Character ranges to anchor to (in plain text coordinates). */
  ranges: ICharacterRange[];
}

/**
 * Module augmentation to declare custom commands for TipTap.
 */
declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    paragraphHighlight: {
      /**
       * Sets the highlights to render. Replaces any existing highlights.
       */
      setHighlights: (highlights: ITypedParagraphHighlight[]) => ReturnType;
      /**
       * Clears all highlights.
       */
      clearHighlights: () => ReturnType;
      /**
       * Sets the sticky anchor widgets (portal-only, no background). Replaces
       * any existing anchors; pass an empty array to remove them. Independent
       * of {@link setHighlights}.
       */
      setAnchors: (anchors: IAnchorWidget[]) => ReturnType;
    };
  }
}

/**
 * Storage interface for the ParagraphHighlightExtension.
 */
export interface IParagraphHighlightStorage {
  /** Map of highlight contexts keyed by unique ID */
  highlightedParagraphs: Map<string, IHighlightContext>;
  /** Map of portal containers keyed by highlight ID */
  portalContainers: Map<string, HTMLElement>;
  /** Callback invoked when highlights change */
  onHighlightsChange?: (
    highlights: Map<string, IHighlightContext>,
    containers: Map<string, HTMLElement>,
  ) => void;
  /** Callback invoked when a highlighted paragraph is clicked */
  onHighlightClick?: (context: IHighlightContext) => void;
  /** Current highlights to render */
  currentHighlights: ITypedParagraphHighlight[];
  /** Current sticky anchor widgets to render (independent of highlights) */
  currentAnchors: IAnchorWidget[];
}

/**
 * Configuration options for the ParagraphHighlightExtension.
 */
export interface IParagraphHighlightOptions {
  /**
   * Callback invoked when highlights change.
   * Used by useHighlightPortals to sync React state.
   */
  onHighlightsChange?: IParagraphHighlightStorage['onHighlightsChange'];
  /**
   * Callback invoked when a highlighted paragraph is clicked.
   * Used to propagate click context to React state.
   */
  onHighlightClick?: IParagraphHighlightStorage['onHighlightClick'];
}

/**
 * Plugin key for the ParagraphHighlightExtension.
 */
export const paragraphHighlightPluginKey = new PluginKey('paragraphHighlight');

/**
 * Represents a paragraph boundary in the document.
 */
interface IParagraphInfo {
  /** Document position of paragraph start */
  pos: number;
  /** Document position of paragraph end */
  end: number;
  /** Character offset start in plain text */
  charStart: number;
  /** Character offset end in plain text */
  charEnd: number;
  /** The paragraph node */
  node: ProseMirrorNode;
  /** Index of the paragraph (0-based) */
  index: number;
  /** Position of parent list item (if paragraph is inside a list) */
  listItemPos?: number;
  /** End position of parent list item */
  listItemEnd?: number;
}

/**
 * Finds the nearest ancestor list item for a given position in the document.
 *
 * @param doc - The ProseMirror document
 * @param pos - The position to check
 * @returns Object with list item position and end, or undefined if not in a list
 */
function findParentListItem(
  doc: ProseMirrorNode,
  pos: number,
): { pos: number; end: number } | undefined {
  const $pos = doc.resolve(pos);

  // Walk up the tree to find a listItem ancestor
  for (let depth = $pos.depth; depth > 0; depth--) {
    const node = $pos.node(depth);
    if (node.type.name === 'listItem') {
      const listItemPos = $pos.before(depth);
      return {
        pos: listItemPos,
        end: listItemPos + node.nodeSize,
      };
    }
  }

  return undefined;
}

/**
 * Extracts paragraph information from a ProseMirror document.
 * Also tracks parent list items to enable full-width highlighting within lists.
 *
 * @param doc - The ProseMirror document
 * @returns Array of paragraph info with positions and character offsets
 */
function getParagraphInfo(doc: ProseMirrorNode): IParagraphInfo[] {
  const paragraphs: IParagraphInfo[] = [];
  let charOffset = 0;
  let paragraphIndex = 0;

  doc.descendants((node, pos) => {
    if (node.type.name === 'paragraph') {
      const textLength = node.textContent.length;

      // Check if we're inside a list item using resolved position
      const parentListItem = findParentListItem(doc, pos);

      paragraphs.push({
        pos,
        end: pos + node.nodeSize,
        charStart: charOffset,
        charEnd: charOffset + textLength,
        node,
        index: paragraphIndex,
        listItemPos: parentListItem?.pos,
        listItemEnd: parentListItem?.end,
      });
      charOffset += textLength + 1; // +1 for newline between paragraphs
      paragraphIndex++;
      return false; // Don't descend into paragraph
    }
    return true;
  });

  return paragraphs;
}

/**
 * Finds paragraphs that intersect with any of the given character ranges.
 *
 * @param paragraphs - Array of paragraph info
 * @param ranges - Character ranges to check
 * @returns Array of paragraph info that intersect with the ranges
 */
function findIntersectingParagraphs(
  paragraphs: IParagraphInfo[],
  ranges: ICharacterRange[],
): Array<{ paragraph: IParagraphInfo; range: ICharacterRange }> {
  const results: Array<{ paragraph: IParagraphInfo; range: ICharacterRange }> =
    [];

  for (const range of ranges) {
    for (const para of paragraphs) {
      // Check if range intersects with paragraph
      if (range.start < para.charEnd && range.end > para.charStart) {
        // Check if we already have this paragraph (avoid duplicates)
        const existing = results.find(r => r.paragraph.pos === para.pos);
        if (!existing) {
          results.push({ paragraph: para, range });
        }
      }
    }
  }

  return results;
}

/**
 * Creates a unique ID for a highlight based on type and position.
 *
 * @param type - The highlight type
 * @param paragraphPos - The paragraph position
 * @returns Unique string ID
 */
function createHighlightId(
  type: TParagraphHighlightType,
  paragraphPos: number,
): string {
  return `${type}-${paragraphPos}`;
}

/**
 * Creates a DOM element as a container for React portals.
 *
 * @param id - The highlight ID
 * @returns HTMLElement container
 */
function createPortalContainer(id: string): HTMLElement {
  const container = document.createElement('span');
  container.className = 'highlight-portal-container';
  container.setAttribute('data-highlight-id', id);
  container.setAttribute('contenteditable', 'false');
  return container;
}

/**
 * TipTap extension for full-width paragraph highlighting with React portal support.
 *
 * Usage:
 * 1. Add extension to editor with onHighlightsChange callback
 * 2. Use setHighlights command to set highlights
 * 3. Use useHighlightPortals hook to render React components
 *
 * @example
 * ```tsx
 * const editor = useEditor({
 *   extensions: [
 *     ParagraphHighlightExtension.configure({
 *       onHighlightsChange: (highlights, containers) => {
 *         // Update React state
 *       },
 *     }),
 *   ],
 * });
 *
 * // Set highlights
 * editor.commands.setHighlights([
 *   { type: 'exception', ranges: [{ start: 0, end: 50 }] },
 * ]);
 * ```
 */
export const ParagraphHighlightExtension = Extension.create<
  IParagraphHighlightOptions,
  IParagraphHighlightStorage
>({
  name: 'paragraphHighlight',

  addOptions() {
    return {
      onHighlightsChange: undefined,
      onHighlightClick: undefined,
    };
  },

  addStorage() {
    return {
      highlightedParagraphs: new Map(),
      portalContainers: new Map(),
      onHighlightsChange: undefined,
      onHighlightClick: undefined,
      currentHighlights: [],
      currentAnchors: [],
    };
  },

  onCreate() {
    // onCreate fires asynchronously and can land after useHighlightPortals has
    // already registered its callbacks on storage; only apply options that were
    // actually provided so we never clobber a live subscriber with undefined.
    if (this.options.onHighlightsChange) {
      this.storage.onHighlightsChange = this.options.onHighlightsChange;
    }
    if (this.options.onHighlightClick) {
      this.storage.onHighlightClick = this.options.onHighlightClick;
    }
  },

  addCommands() {
    return {
      /**
       * Sets the highlights to render.
       * This replaces any existing highlights.
       */
      setHighlights:
        (highlights: ITypedParagraphHighlight[]) =>
        ({
          tr,
          dispatch,
        }: {
          tr: Transaction;
          dispatch?: (tr: Transaction) => void;
        }) => {
          if (dispatch) {
            // Store highlights in extension storage
            this.storage.currentHighlights = highlights;
            // Trigger decoration recalculation by dispatching transaction with metadata
            tr.setMeta(paragraphHighlightPluginKey, { highlights });
            dispatch(tr);
          }
          return true;
        },

      /**
       * Clears all highlights.
       */
      clearHighlights:
        () =>
        ({
          tr,
          dispatch,
        }: {
          tr: Transaction;
          dispatch?: (tr: Transaction) => void;
        }) => {
          if (dispatch) {
            this.storage.currentHighlights = [];
            tr.setMeta(paragraphHighlightPluginKey, { highlights: [] });
            dispatch(tr);
          }
          return true;
        },

      /**
       * Sets the sticky anchor widgets. Independent of setHighlights, so it
       * does not disturb (and is not disturbed by) the hover/exception set.
       */
      setAnchors:
        (anchors: IAnchorWidget[]) =>
        ({
          tr,
          dispatch,
        }: {
          tr: Transaction;
          dispatch?: (tr: Transaction) => void;
        }) => {
          if (dispatch) {
            this.storage.currentAnchors = anchors;
            tr.setMeta(paragraphHighlightPluginKey, { anchors });
            dispatch(tr);
          }
          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    const storage = this.storage;

    return [
      new Plugin({
        key: paragraphHighlightPluginKey,

        state: {
          init: () => ({
            highlights: [] as ITypedParagraphHighlight[],
            anchors: [] as IAnchorWidget[],
          }),

          apply: (tr, value) => {
            const meta = tr.getMeta(paragraphHighlightPluginKey);
            // Highlights and anchors are set independently; merge whichever the
            // transaction carried and keep the other untouched.
            if (meta?.highlights !== undefined || meta?.anchors !== undefined) {
              return {
                highlights: meta.highlights ?? value.highlights,
                anchors: meta.anchors ?? value.anchors,
              };
            }
            // If document changed, keep current state
            return value;
          },
        },

        props: {
          handleClick: (_view, pos) => {
            // Check if the clicked position falls within a highlighted paragraph
            for (const [, context] of storage.highlightedParagraphs) {
              if (pos >= context.paragraphPos && pos <= context.paragraphEnd) {
                storage.onHighlightClick?.(context);
                return false; // Don't prevent default editor behavior
              }
            }
            return false;
          },

          decorations: state => {
            const pluginState = paragraphHighlightPluginKey.getState(state);
            const highlights: ITypedParagraphHighlight[] =
              pluginState?.highlights || storage.currentHighlights || [];
            const anchors: IAnchorWidget[] =
              pluginState?.anchors || storage.currentAnchors || [];

            if (highlights.length === 0 && anchors.length === 0) {
              // Clear storage when nothing to render
              if (storage.highlightedParagraphs.size > 0) {
                storage.highlightedParagraphs.clear();
                storage.portalContainers.clear();
                storage.onHighlightsChange?.(
                  storage.highlightedParagraphs,
                  storage.portalContainers,
                );
              }
              return DecorationSet.empty;
            }

            const { doc } = state;
            const decorations: Decoration[] = [];
            const newHighlightedParagraphs = new Map<
              string,
              IHighlightContext
            >();
            const newPortalContainers = new Map<string, HTMLElement>();

            // Get paragraph info from document
            const paragraphs = getParagraphInfo(doc);

            // Sort highlights so exception types are processed first;
            // this ensures exception decorations take priority over
            // resolvedException on the same paragraph.
            const sortedHighlights = [...highlights].sort((a, b) => {
              if (a.type === 'exception' && b.type !== 'exception') return -1;
              if (a.type !== 'exception' && b.type === 'exception') return 1;
              return 0;
            });

            for (const highlight of sortedHighlights) {
              const { type, ranges } = highlight;

              if (!PARAGRAPH_HIGHLIGHT_TYPES.includes(type)) {
                continue;
              }

              // Process each range individually to determine last paragraph per range
              for (const range of ranges) {
                // Find paragraphs that intersect with this specific range
                const intersecting = findIntersectingParagraphs(paragraphs, [
                  range,
                ]);

                // Filter out paragraphs with no text content
                const validParagraphs = intersecting.filter(({ paragraph }) =>
                  paragraph.node.textContent.trim(),
                );

                if (validParagraphs.length === 0) {
                  continue;
                }

                // Find the last paragraph in this range (highest paragraph index)
                const lastParagraphIndex = Math.max(
                  ...validParagraphs.map(({ paragraph }) => paragraph.index),
                );

                for (const { paragraph } of validParagraphs) {
                  const id = createHighlightId(type, paragraph.pos);

                  // Skip if we already processed this paragraph for this type
                  if (newHighlightedParagraphs.has(id)) {
                    continue;
                  }

                  // Exception takes priority: skip resolved decoration when
                  // the same paragraph already has an active exception highlight.
                  if (
                    type === 'resolvedException' &&
                    newHighlightedParagraphs.has(
                      createHighlightId('exception', paragraph.pos),
                    )
                  ) {
                    continue;
                  }

                  // Determine decoration target: use list item if paragraph is inside one
                  // This keeps the bullet point and content on the same line
                  const decorationStart =
                    paragraph.listItemPos ?? paragraph.pos;
                  const decorationEnd = paragraph.listItemEnd ?? paragraph.end;
                  const isInList = paragraph.listItemPos !== undefined;

                  // Create node decoration for full-width background
                  const nodeDecoration = Decoration.node(
                    decorationStart,
                    decorationEnd,
                    {
                      class: `paragraph-highlight paragraph-highlight-${type}${isInList ? ' paragraph-highlight-list-item' : ''}`,
                      'data-highlight-type': type,
                    },
                  );
                  decorations.push(nodeDecoration);

                  // Determine if this is the last paragraph in the range
                  const isLastInRange = paragraph.index === lastParagraphIndex;

                  // An after-block type needs a single portal, on the range's
                  // last paragraph — the only one whose portal renders anything
                  // (see `isLastInRange` in renderHighlight). The band above is
                  // already painted for every paragraph in the range.
                  const afterBlock = AFTER_BLOCK_PORTAL_TYPES.includes(type);
                  if (afterBlock && !isLastInRange) continue;

                  // Reuse existing container or create new one
                  // This is critical - ProseMirror recreates decorations on every transaction,
                  // but React portals need stable DOM references
                  const existingContainer = storage.portalContainers.get(id);
                  const container =
                    existingContainer || createPortalContainer(id);

                  // Inline types sit just before the paragraph's closing tag (not
                  // the list item); after-block types escape the paragraph's
                  // wrapper, so a callout can't swallow the widget.
                  const widgetPos = afterBlock
                    ? positionAfterContainingBlock(doc, paragraph)
                    : paragraph.end - 1;
                  const widgetDecoration = Decoration.widget(
                    widgetPos,
                    container,
                    {
                      side: 1,
                      key: id,
                    },
                  );
                  decorations.push(widgetDecoration);

                  // Store context for React components
                  const context: IHighlightContext = {
                    id,
                    type,
                    paragraphPos: paragraph.pos,
                    paragraphEnd: paragraph.end,
                    text: paragraph.node.textContent,
                    originalRange: range,
                    paragraphIndex: paragraph.index,
                    isLastInRange,
                    source: highlight.source,
                  };

                  newHighlightedParagraphs.set(id, context);
                  newPortalContainers.set(id, container);
                }
              }
            }

            // Anchor widgets: portal-only, no background row. Processed in a
            // separate pass so the highlight loop above stays untouched and an
            // anchor never paints a node decoration. Ids are prefixed
            // `browserOperator-` so styles.css can position them.
            for (const anchor of anchors) {
              for (const range of anchor.ranges) {
                const intersecting = findIntersectingParagraphs(paragraphs, [
                  range,
                ]);
                const validParagraphs = intersecting.filter(({ paragraph }) =>
                  paragraph.node.textContent.trim(),
                );
                if (validParagraphs.length === 0) {
                  continue;
                }
                const lastParagraphIndex = Math.max(
                  ...validParagraphs.map(({ paragraph }) => paragraph.index),
                );

                for (const { paragraph } of validParagraphs) {
                  const id = `browserOperator-${anchor.id}-${paragraph.pos}`;
                  if (newHighlightedParagraphs.has(id)) {
                    continue;
                  }

                  const existingContainer = storage.portalContainers.get(id);
                  const container =
                    existingContainer || createPortalContainer(id);

                  const widgetPos = paragraph.end - 1;
                  decorations.push(
                    Decoration.widget(widgetPos, container, {
                      side: 1,
                      key: id,
                    }),
                  );

                  newHighlightedParagraphs.set(id, {
                    id,
                    type: 'browserOperator',
                    paragraphPos: paragraph.pos,
                    paragraphEnd: paragraph.end,
                    text: paragraph.node.textContent,
                    originalRange: range,
                    paragraphIndex: paragraph.index,
                    isLastInRange: paragraph.index === lastParagraphIndex,
                  });
                  newPortalContainers.set(id, container);
                }
              }
            }

            // Check if highlights have actually changed (not just a decoration recalculation)
            const oldIds = Array.from(storage.highlightedParagraphs.keys())
              .sort()
              .join(',');
            const newIds = Array.from(newHighlightedParagraphs.keys())
              .sort()
              .join(',');
            const hasChanged = oldIds !== newIds;

            // Update storage
            storage.highlightedParagraphs = newHighlightedParagraphs;
            storage.portalContainers = newPortalContainers;

            // Only notify React when highlights have actually changed
            // This prevents unnecessary re-renders on cursor movements, etc.
            if (hasChanged) {
              requestAnimationFrame(() => {
                storage.onHighlightsChange?.(
                  newHighlightedParagraphs,
                  newPortalContainers,
                );
              });
            }

            return DecorationSet.create(doc, decorations);
          },
        },
      }),
    ];
  },
});

export default ParagraphHighlightExtension;
